// Correct Score Probability Matrix — first renderer ported to React (Phase 3).
//
// Emits the SAME DOM + class names as the legacy renderCorrectScoreModule, so
// the shared CSS renders it identically (verified by the visual A/B test). The
// probability model is pure and lives in @bento/shared; this component is just
// data-in → JSX-out. Rows go away-goals 4→0 (top→bottom), columns home-goals
// 0→4 (left→right), matching the legacy grid order.

import { Fragment } from "react";
import { heatmapCellColor, scoreLabelPct, CS_MAX_GOALS } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface CorrectScoreProps {
  /** The match, e.g. "Arsenal v Everton". */
  fixture: string;
}

export default function CorrectScore({ fixture }: CorrectScoreProps) {
  const {
    data,
    isPending,
    isError,
    refetch,
  } = useWidgetData("correct-score", useFixtureKey(fixture));

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const { grid, maxP } = data;

  const homes = Array.from({ length: CS_MAX_GOALS + 1 }, (_, h) => h); // 0→4
  const aways = [...homes].reverse(); // 4→0

  return (
    <div className="data-wrapper csprob-wrapper">
      <div className="csprob-grid-area">
        <div className="csprob-yaxis-label">Away</div>
        <div className="csprob-inner">
          <div className="heatmap-grid">
            {aways.map((a) => (
              <Fragment key={a}>
                <div className="hm-label">{a}</div>
                {homes.map((h) => {
                  const p = grid[h]?.[a] ?? 0;
                  return (
                    <div className="hm-cell" key={h}>
                      <div
                        className="hm-cell-bg bento-fill"
                        style={{ background: heatmapCellColor(p, maxP) }}
                      />
                      <span className="hm-cell-label">{scoreLabelPct(p)}</span>
                    </div>
                  );
                })}
              </Fragment>
            ))}
            <div />
            {homes.map((h) => (
              <div className="hm-label" key={h}>
                {h}
              </div>
            ))}
          </div>
          <div className="csprob-xaxis-label">Home</div>
        </div>
      </div>
      <div className="cs-footer">Most likely outcome, based on Variance Model</div>
    </div>
  );
}
